// =============================================================================
// OCR Service — Tesseract.js wrapper with preprocessing and structured output.
// Lazy singleton worker: initialized once, reused across invocations.
// =============================================================================

import { createWorker } from 'tesseract.js';

let worker = null;

/**
 * Initialize the Tesseract worker (called automatically on first recognize).
 */
export async function initOCR() {
  if (!worker) {
    worker = await createWorker('eng');
  }
}

// ---------------------------------------------------------------------------
// Image helpers
// ---------------------------------------------------------------------------

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    if (src instanceof Blob) {
      const url = URL.createObjectURL(src);
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
      img.src = url;
    } else {
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = src;
    }
  });
}

/** Grayscale + contrast boost on a canvas (in-place). */
function preprocessCanvas(canvas, { binarize = false, binarizeThreshold = 140 } = {}) {
  const ctx = canvas.getContext('2d');
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const gray = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    const c = Math.min(255, Math.max(0, (gray - 128) * 1.5 + 128));
    d[i] = d[i + 1] = d[i + 2] = c;
  }
  if (binarize) {
    for (let i = 0; i < d.length; i += 4) {
      const v = d[i] >= binarizeThreshold ? 255 : 0;
      d[i] = d[i + 1] = d[i + 2] = v;
    }
  }
  ctx.putImageData(imageData, 0, 0);
}

/** Remove top ~12 % and bottom ~12 % (status bar / footer chrome). */
function cropContent(img) {
  const canvas = document.createElement('canvas');
  const top = Math.round(img.height * 0.12);
  const bot = Math.round(img.height * 0.12);
  const h = img.height - top - bot;
  canvas.width = img.width;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, top, img.width, h, 0, 0, img.width, h);
  return canvas;
}

/** Headline crop: top 45 % of the content area (after chrome removal). */
function cropHeadline(img) {
  const canvas = document.createElement('canvas');
  const top = Math.round(img.height * 0.12);
  const contentH = img.height - top - Math.round(img.height * 0.12);
  const h = Math.round(contentH * 0.45);
  canvas.width = img.width;
  canvas.height = h;
  canvas.getContext('2d').drawImage(img, 0, top, img.width, h, 0, 0, img.width, h);
  return canvas;
}

/** Run Tesseract on a canvas and return structured lines. */
async function ocrCanvas(canvas) {
  await initOCR();
  const { data } = await worker.recognize(canvas);
  const lines = (data.lines || []).map(line => ({
    text: line.text.trim(),
    confidence: line.confidence,
    bbox: line.bbox,
    yNorm: canvas.height > 0 ? (line.bbox.y0 + line.bbox.y1) / 2 / canvas.height : 0.5,
    heightNorm: canvas.height > 0 ? (line.bbox.y1 - line.bbox.y0) / canvas.height : 0,
  })).filter(l => l.text.length > 0);
  return { text: data.text || '', confidence: data.confidence || 0, lines };
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Recognize text from an image with auto-crop and preprocessing.
 * Returns { text, confidence, lines, headlineLines }.
 */
export async function recognizeText(imageSource, options = {}) {
  const img = await loadImage(imageSource);

  const contentCanvas = cropContent(img);
  preprocessCanvas(contentCanvas, options);
  const main = await ocrCanvas(contentCanvas);

  const headCanvas = cropHeadline(img);
  preprocessCanvas(headCanvas, options);
  const head = await ocrCanvas(headCanvas);

  console.log('[OCR] Confidence:', main.confidence.toFixed(1), '% | Lines:', main.lines.length);
  console.log('[OCR] Full text:', main.text.slice(0, 500));
  if (main.confidence < 60) {
    console.warn('[OCR] Low confidence — text may be garbled. First 3 lines:',
      main.lines.slice(0, 3).map(l => `"${l.text}" (conf: ${l.confidence.toFixed(0)})`));
  }

  return {
    text: main.text,
    confidence: main.confidence,
    lines: main.lines,
    headlineLines: head.lines,
  };
}

/**
 * Re-run OCR on a user-defined crop region.
 * @param {object} cropRect – { x, y, width, height } normalised 0-1.
 */
export async function recognizeTextWithCrop(imageSource, cropRect) {
  const img = await loadImage(imageSource);
  const canvas = document.createElement('canvas');
  const sx = Math.round(cropRect.x * img.width);
  const sy = Math.round(cropRect.y * img.height);
  const sw = Math.round(cropRect.width * img.width);
  const sh = Math.round(cropRect.height * img.height);
  canvas.width = sw;
  canvas.height = sh;
  canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
  preprocessCanvas(canvas);
  const main = await ocrCanvas(canvas);

  const hh = Math.round(sh * 0.45);
  const hCanvas = document.createElement('canvas');
  hCanvas.width = sw;
  hCanvas.height = hh;
  hCanvas.getContext('2d').drawImage(canvas, 0, 0, sw, hh, 0, 0, sw, hh);
  const head = await ocrCanvas(hCanvas);

  return {
    text: main.text,
    confidence: main.confidence,
    lines: main.lines,
    headlineLines: head.lines,
  };
}

/**
 * Terminate the Tesseract worker.
 */
export async function terminateOCR() {
  if (worker) {
    await worker.terminate();
    worker = null;
  }
}
